import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { IconType } from "@edaz/edazkit/Icon";
import { observer } from "mobx-react-lite";
import classnames from "classnames";
import React, { FormEvent, FunctionComponent, useCallback, useEffect, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import Button from "@edaz/edazkit/Button";
import ReactMarkdown from "react-markdown";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import { IChargeManagementEditPartViewModel } from "viewModels/chargeManagement/chargeManagementEdit/types";
import Collapsible from "../../common/collapsible/collapsible";
import { preventDefault, View } from "@frui.ts/views";
import Tooltip from "controls/tooltip";
import { getTimeZoneLabel } from "utils/dateUtils";
import { isMobile } from "react-device-detect";
import ChargeChangeRejectionReason from "entities/chargeChangeRejectionReason";
import ChargeManagementEditViewModel from "viewModels/chargeManagement/chargeManagementEdit/chargeManagementEditViewModel";

const ChargeEditForm: FunctionComponent<{
  vm: ScreenBase & IChargeManagementEditPartViewModel;
  vmParent: ChargeManagementEditViewModel;
  localize: (code: string) => string;
  collapsed: boolean;
  onSubmit?: (event: FormEvent, token?: string) => void;
  onValidate?: () => void;
  onCancelSecondStep?: () => void;
  showSecondStep?: boolean;
  secondStepLoading?: boolean;
  secondStepErrorMessage?: string;
  hideConfirmChangeButton?: boolean;
  warningMessages?: string[];
  extendedLoading?: boolean; // Pro 2.5s loading
  toggle: () => void;
  icon: IconType;
  children?: React.ReactNode;
  warning?: boolean;
  info?: boolean;
  toolbox?: boolean;
  className?: string;
  canEdit: boolean;
  afterValidityStart?: boolean;
  afterValidity?: boolean;
  notAllowedBySourceType?: boolean;
  success?: boolean;
  customSuccessMessage?: string;
  rejectedReason?: string;
  chargeType?: string;
  customInfoMessage?: string;
  errorMessage?: string;
  name?: IChargeManagementEditPartViewModel | ScreenBase;
}> = observer(
  ({
    vm,
    vmParent,
    localize,
    children,
    icon,
    warning = true,
    info = true,
    toolbox = true,
    className,
    collapsed,
    toggle,
    onSubmit,
    onCancelSecondStep,
    showSecondStep = false,
    secondStepLoading = false,
    secondStepErrorMessage = undefined,
    hideConfirmChangeButton = false,
    warningMessages = [],
    extendedLoading = false,
    canEdit,
    afterValidityStart = false,
    afterValidity = false,
    notAllowedBySourceType = false,
    success,
    customSuccessMessage,
    rejectedReason,
    chargeType,
    customInfoMessage,
    errorMessage,
    name,
  }) => {
    const formRef = useCallback(node => {
      if (node !== null) {
        vm.formElement = node;
      }
    }, []);
    const infoMessage = rejectedReason
      ? rejectedReason === ChargeChangeRejectionReason.DeniedForChargeType
        ? localize(rejectedReason).replace("%CHARGE_TYPE%", chargeType ?? "")
        : localize(rejectedReason)
      : localize("successInfo");

    const resetSecondStep = () => {
      vm.showSecondStep = false;
      vm.warningMessages = [];
      vm.overlappingCharges = undefined;
      vm.overlappingCharges = undefined;
      vm.secondStepErrorMessage = undefined;
      if (vm.turnstileViewModel) {
        vm.turnstileViewModel.isDisabledByTurnsitel = false;
        vm.turnstileViewModel.reset();
      }
      onCancelSecondStep?.();
    };

    const distinct = (value: string, index: number, array: string[]) => {
      return array.indexOf(value) === index;
    };

    const translateOverlappingCharge = (charge: any) => {
      const renderDateWithTimeZone = (date: Date) => {
        const formatted = charge.formatDateTime(date);
        return !charge.isDifferentTimeZone ? (
          <span>{formatted}</span>
        ) : (
          <>
            <span>{formatted}</span>
            <Tooltip
              id="cet-tooltip"
              placement={isMobile ? "bottom" : "right"}
              tooltip={charge.translateGeneral("timezoneTooltip")}
              trigger={["hover", "focus"]}>
              <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {getTimeZoneLabel(date)}
              </span>
            </Tooltip>
          </>
        );
      };

      return (
        <span>
          {charge.translateGeneral("charge.management.edit.validity.overlappingChargeStart")}
          {renderDateWithTimeZone(charge.validSince)}
          {charge.translateGeneral("charge.management.edit.validity.overlappingChargeEnd")}
          {renderDateWithTimeZone(charge.validUntil)}
          {"."}
        </span>
      );
    };
    const showAlert = warning && !customInfoMessage && canEdit && !rejectedReason && !notAllowedBySourceType && !afterValidity;
    const showChargeNotEditableInfo =
      info && !customInfoMessage && (!canEdit || rejectedReason || notAllowedBySourceType || afterValidity);
    const showEditForm = canEdit && !rejectedReason;
    const showConfirmChangeButton = !hideConfirmChangeButton && toolbox && canEdit && !secondStepLoading && !showSecondStep;
    const showConfirmChangeSecondStepButton = toolbox && canEdit && !secondStepLoading && showSecondStep;

    return (
      <div className={classnames("py-2", "py-lg-3", className)}>
        <Collapsible
          title={localize("title")}
          collapse={vm.translate("collapse")}
          collapsed={collapsed}
          icon={icon}
          toggle={toggle}>
          <Form ref={formRef} onSubmit={onSubmit} className="mt-4 mb-4">
            {showAlert && <AlertWithInfoIcon variant="warning">{localize("alert")}</AlertWithInfoIcon>}
            {errorMessage && (
              <Alert variant="danger" className={"border-none text-center mb-4 is-invalid"}>
                {errorMessage}
              </Alert>
            )}
            {success && customSuccessMessage && (
              <AlertWithInfoIcon variant="success">{customSuccessMessage || localize("success")}</AlertWithInfoIcon>
            )}
            {showChargeNotEditableInfo && (
              <AlertWithInfoIcon variant="warning">
                <ReactMarkdown className="markdown" linkTarget="_blank">
                  {infoMessage}
                </ReactMarkdown>
              </AlertWithInfoIcon>
            )}
            {customInfoMessage && <AlertWithInfoIcon variant="warning">{customInfoMessage}</AlertWithInfoIcon>}
            {showEditForm ? children : undefined}
            {secondStepErrorMessage && (
              <AlertWithInfoIcon variant="danger">
                <ReactMarkdown className="font-weight-400 markdown" linkTarget="_blank">
                  {secondStepErrorMessage}
                </ReactMarkdown>
              </AlertWithInfoIcon>
            )}
            {!success &&
              secondStepErrorMessage === undefined &&
              warningMessages.filter(distinct).map(message => (
                <AlertWithInfoIcon variant="warning" key={message}>
                  {message}
                </AlertWithInfoIcon>
              ))}

            {!success && secondStepErrorMessage === undefined && vm.overlappingCharges && vm.overlappingCharges.length > 0 && (
              <AlertWithInfoIcon variant="warning">
                {vm.overlappingCharges.map((charge, index) => {
                  return <p key={index}>{translateOverlappingCharge(charge)}</p>;
                })}
              </AlertWithInfoIcon>
            )}

            <div>
              {(showConfirmChangeButton || showConfirmChangeSecondStepButton) && <View vm={vm.turnstileViewModel} />}

              {showConfirmChangeButton && (
                <Button
                  type="button"
                  className="d-block mx-auto mt-2 mb-2 text-wrap mw-100"
                  disabled={!vm.canSubmit || vm.turnstileViewModel.isDisabledByTurnsitel}
                  onClick={async e => {
                    if (!vm.validate()) {
                      return;
                    }
                    e.preventDefault();

                    if (vm.hasSecondStep) {
                      if (onSubmit) {
                        onSubmit(e);
                      }
                      return;
                    }

                    vm.turnstileViewModel.showInteractive = true;
                    let token: string | undefined;
                    if (vm.turnstileViewModel?.turnstileRef?.current?.execute) {
                      vm.turnstileViewModel.onTurnstileLoad();
                      vm.turnstileViewModel.isDisabledByTurnsitel = true;
                      await vm.turnstileViewModel.turnstileRef.current.execute();
                      token = await vm.turnstileViewModel.turnstileRef.current.getResponsePromise();
                      if (token) {
                        vm.turnstileViewModel.onTurnstileVerify(token);
                        await new Promise(resolve => setTimeout(resolve, 10));
                      }
                    }
                    if (token && onSubmit) {
                      vm.turnstileViewModel.isDisabledByTurnsitel = false;
                      vm.turnstileViewModel.onSetInfo(vm.turnstileViewModel.translateTurnstile("success"));
                      onSubmit(e, token);
                      vm.turnstileViewModel.reset();
                    }
                  }}>
                  {vm.hasSecondStep ? localize("continue") : localize("confirmChange")}
                </Button>
              )}
              {secondStepLoading && (
                <div className="d-flex align-items-center justify-content-center">
                  <div className="spinner-border spinner-border-sm text-success" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div className="ml-3">{localize("validatingLicensePlate")}</div>
                </div>
              )}
              {showConfirmChangeSecondStepButton && (
                <div className="d-flex flex-column-reverse flex-sm-row justify-content-center mt-2">
                  <Button
                    type="button"
                    variant="light"
                    onClick={preventDefault(resetSecondStep)}
                    className="d-block mx-2 text-wrap">
                    {localize("cancelChange")}
                  </Button>
                  <Button
                    type="button"
                    className="d-block mx-2 mb-3 mb-sm-0 text-wrap"
                    disabled={
                      !vm.canSubmit || vm.turnstileViewModel.isDisabledByTurnsitel || secondStepErrorMessage !== undefined
                    }
                    onClick={async e => {
                      if (!vm.validate()) {
                        return;
                      }
                      e.preventDefault();
                      vm.turnstileViewModel.showInteractive = true;
                      if (vm.turnstileViewModel?.turnstileRef?.current?.execute) {
                        vm.turnstileViewModel.onTurnstileLoad();
                        vm.turnstileViewModel.isDisabledByTurnsitel = true;
                        await vm.turnstileViewModel.turnstileRef.current.execute();
                        const token = await vm.turnstileViewModel.turnstileRef.current.getResponsePromise();
                        if (token) {
                          vm.turnstileViewModel.onTurnstileVerify(token);
                          await new Promise(resolve => setTimeout(resolve, 10));
                        }

                        if (onSubmit && token) {
                          vm.turnstileViewModel.isDisabledByTurnsitel = false;
                          vm.turnstileViewModel.onSetInfo(vm.turnstileViewModel.translateTurnstile("success"));
                          onSubmit(e, token);
                          vm.turnstileViewModel.reset();
                        }
                      }
                    }}>
                    {localize("confirmChange")}
                  </Button>
                </div>
              )}
            </div>
          </Form>
        </Collapsible>
      </div>
    );
  }
);

export default ChargeEditForm;
